import React from "react";
import { Navigate } from "react-router-dom";
import { Box, Fade } from "@mui/material";
import { useAppSelector } from "../store/store";
import { selectIsAuthenticated } from "../store/slices/authSlice";
import { ROUTES } from "../constants/routes";

interface PublicRouteProps {
  children: React.ReactNode;
  redirectIfAuthenticated?: boolean;
  redirectTo?: string;
}

/**
 * PublicRoute Component
 *
 * Wrapper for public routes that don't require authentication.
 * Optionally redirects authenticated users (e.g., login page → dashboard).
 *
 * Features:
 * - Smooth fade transitions
 * - Optional redirect for authenticated users
 * - Customizable redirect path
 *
 * @example
 * // Basic public route (accessible by all)
 * <PublicRoute>
 *   <Landing />
 * </PublicRoute>
 *
 * // Login page (redirect authenticated users to dashboard)
 * <PublicRoute redirectIfAuthenticated redirectTo="/app/dashboard">
 *   <Login />
 * </PublicRoute>
 */
const PublicRoute: React.FC<PublicRouteProps> = ({
  children,
  redirectIfAuthenticated = false,
  redirectTo = ROUTES.APP_DASHBOARD,
}) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  // If user is authenticated and this route should redirect authenticated users
  // (e.g., login page), redirect to dashboard
  if (isAuthenticated && redirectIfAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  // Show public content with smooth transition
  return (
    <Fade in={true} timeout={400}>
      <Box>{children}</Box>
    </Fade>
  );
};

export default PublicRoute;
